import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Home from './pages/Home';
import Clients from './pages/Clients';
import ClientsList from './pages/ClientsList';
import ClientDetail from './pages/ClientDetail';
import Vendor from './pages/Vendor';
import VendorsList from './pages/VendorsList';
import Vehicles from './pages/Vehicles';
import VehiclesList from './pages/VehiclesList';
import Inventory from './pages/Inventory';
import Parts from './pages/Parts';
import AddPart from './pages/AddPart';
import AddAlternativePart from './pages/AddAlternativePart';
import IssueParts from './pages/IssueParts';
import IssuedPartsList from './pages/IssuedPartsList';
import Tires from './pages/Tires';
import Labor from './pages/Labor';
import AddLaborService from './pages/AddLaborService';
import CannedJobs from './pages/CannedJobs';
import JobCards from './pages/JobCards';
import NewJobCard from './pages/NewJobCard';
import InspectionsList from './pages/InspectionsList';
import Inspection from './pages/Inspection';
import InspectionChecklist from './pages/InspectionChecklist';
import DamageReport from './pages/DamageReport';
import BusinessAssets from './pages/BusinessAssets';
import Purchase from './pages/Purchase';
import Purchases from './pages/Purchases';
import PurchaseBarcodePrint from './pages/PurchaseBarcodePrint';
import InventoryReports from './pages/InventoryReports';
import TimeTracking from './pages/TimeTracking';
import Employees from './pages/Employees';
import NewEmployee from './pages/NewEmployee';
import Accounting from './pages/Accounting';
import InvoicesList from './pages/InvoicesList';
import Invoice from './pages/Invoice';
import EstimatesList from './pages/EstimatesList';
import Appointments from './pages/Appointments';
import BayBoard from './pages/BayBoard';
import ServiceReminders from './pages/ServiceReminders';
import HrModule from './pages/HrModule';
import Expenses from './pages/Expenses';
import LanguageSwitcher from './components/LanguageSwitcher';
import AutoTranslate from './components/AutoTranslate';
import { canAccessPath, getCurrentAppUser } from './utils/accessControl';
import './App.css';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const currentUser = getCurrentAppUser();

  const handleLogin = (session) => {
    localStorage.setItem('token', session.token);
    if (session.user) {
      localStorage.setItem('user', JSON.stringify(session.user));
    }
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
  };

  const renderProtected = (path, element) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (!canAccessPath(path, currentUser)) {
      return <Navigate to="/" replace />;
    }

    return element;
  };

  return (
    <BrowserRouter>
      <LanguageSwitcher />
      <AutoTranslate />
      <Routes>
        <Route
          path="/accounting"
          element={renderProtected('/accounting', <Accounting onLogout={handleLogout} />)}
        />
        <Route
          path="/invoices"
          element={renderProtected('/invoices', <InvoicesList />)}
        />
        <Route
          path="/invoices/:id"
          element={renderProtected('/invoices/:id', <Invoice />)}
          />
        <Route
          path="/estimates"
          element={renderProtected('/estimates', <EstimatesList onLogout={handleLogout} />)}
        />
        <Route
          path="/estimates/new"
          element={renderProtected('/estimates/new', <NewJobCard onLogout={handleLogout} documentType="Estimate" />)}
        />
        <Route
          path="/estimates/:id/edit"
          element={renderProtected('/estimates/:id/edit', <NewJobCard onLogout={handleLogout} documentType="Estimate" />)}
        />
        <Route
          path="/appointments"
          element={renderProtected('/appointments', <Appointments onLogout={handleLogout} />)}
        />
        <Route
          path="/service-reminders"
          element={renderProtected('/service-reminders', <ServiceReminders onLogout={handleLogout} />)}
        />
        <Route
          path="/hr"
          element={renderProtected('/hr', <HrModule onLogout={handleLogout} />)}
        />
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" /> : <Login onLogin={handleLogin} />}
        />
        <Route
          path="/"
          element={isAuthenticated ? <Home onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/clients"
          element={renderProtected('/clients', <ClientsList />)}
        />
        <Route
          path="/clients/new"
          element={renderProtected('/clients/new', <Clients />)}
        />
        <Route
          path="/clients/:id"
          element={renderProtected('/clients/:id', <ClientDetail />)}
        />
        <Route
          path="/vendors"
          element={renderProtected('/vendors', <VendorsList />)}
        />
        <Route
          path="/vendors/new"
          element={renderProtected('/vendors/new', <Vendor />)}
        />
        <Route
          path="/vehicles"
          element={renderProtected('/vehicles', <VehiclesList />)}
        />
        <Route
          path="/vehicles/new"
          element={renderProtected('/vehicles/new', <Vehicles />)}
        />
        <Route
          path="/vehicles/:id/edit"
          element={renderProtected('/vehicles/:id/edit', <Vehicles />)}
        />
        <Route
          path="/inventory"
          element={renderProtected('/inventory', <Inventory onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/parts"
          element={renderProtected('/inventory/parts', <Parts onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/issue-parts"
          element={renderProtected('/inventory/issue-parts', <IssueParts onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/issued-parts"
          element={renderProtected('/inventory/issued-parts', <IssuedPartsList onLogout={handleLogout} />)}
        />
        <Route
          path="/add-part"
          element={renderProtected('/add-part', <AddPart />)}
        />
        <Route
          path="/add-alternative-part"
          element={renderProtected('/add-alternative-part', <AddAlternativePart />)}
        />
        <Route
          path="/inventory/tires"
          element={renderProtected('/inventory/tires', <Tires onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/labor"
          element={renderProtected('/inventory/labor', <Labor onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/labor/new"
          element={renderProtected('/inventory/labor/new', <AddLaborService />)}
        />
        <Route
          path="/inventory/canned-jobs"
          element={renderProtected('/inventory/canned-jobs', <CannedJobs onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/business-assets"
          element={renderProtected('/inventory/business-assets', <BusinessAssets onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/purchase"
          element={renderProtected('/inventory/purchase', <Purchase onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/purchases"
          element={renderProtected('/inventory/purchases', <Purchases onLogout={handleLogout} />)}
        />
        <Route
          path="/inventory/purchases/:id/barprint"
          element={renderProtected('/inventory/purchases/:id/barprint', <PurchaseBarcodePrint />)}
        />
        <Route
          path="/reports"
          element={renderProtected('/reports', <InventoryReports onLogout={handleLogout} />)}
        />
        <Route
          path="/expenses"
          element={renderProtected('/expenses', <Expenses onLogout={handleLogout} />)}
        />
        <Route
          path="/time-tracking"
          element={renderProtected('/time-tracking', <TimeTracking />)}
        />
        <Route
          path="/time-tracking/employees"
          element={renderProtected('/time-tracking/employees', <Employees />)}
        />
        <Route
          path="/time-tracking/employees/new"
          element={renderProtected('/time-tracking/employees/new', <NewEmployee />)}
        />
        <Route
          path="/jobcards"
          element={renderProtected('/jobcards', <JobCards onLogout={handleLogout} />)}
        />
        <Route
          path="/bays"
          element={renderProtected('/bays', <BayBoard onLogout={handleLogout} />)}
        />
        <Route
          path="/jobcards/new"
          element={renderProtected('/jobcards/new', <NewJobCard onLogout={handleLogout} />)}
        />
        <Route
          path="/jobcards/:id/edit"
          element={renderProtected('/jobcards/:id/edit', <NewJobCard onLogout={handleLogout} />)}
        />
        <Route
          path="/inspection"
          element={renderProtected('/inspection', <InspectionsList onLogout={handleLogout} />)}
        />
        <Route
          path="/inspection/new"
          element={renderProtected('/inspection/new', <Inspection onLogout={handleLogout} />)}
        />
        <Route
          path="/inspection/checklist"
          element={renderProtected('/inspection/checklist', <InspectionChecklist onLogout={handleLogout} />)}
        />
        <Route
          path="/inspection/damage"
          element={renderProtected('/inspection/damage', <DamageReport onLogout={handleLogout} />)}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
